import moment from 'moment';
import Constants from './Constants';
import { BUY } from './AuctionTypes';
import { getBidCancellationReason } from './BidCancellationReasons';
import { createUserValue as createUserValueFromUser } from './index';

export const createUserValue = (bid, useName) => createUserValueFromUser(bid.user, useName);

export const getBidAllocationCoefficient = (bid) => {
    const FULLY_ALLOCATED = 2;
    const PARTIALLY_ALLOCATED = 1;
    const NOT_ALLOCATED = 0;
    let result = NOT_ALLOCATED;
    if (bid.winner || (bid.quantityAllocated > 0 && bid.quantityAllocated === bid.quantity)) {
        result = FULLY_ALLOCATED;
    } else if (bid.quantityAllocated > 0) {
        result = PARTIALLY_ALLOCATED;
    }
    return result;
};

export const priceComparator = (bid1, bid2, auctionType) => {
    let result = bid1.price - bid2.price;
    if (result === 0) {
        const bid1Coefficient = getBidAllocationCoefficient(bid1);
        const bid2Coefficient = getBidAllocationCoefficient(bid2);
        result = bid1Coefficient - bid2Coefficient;
        if (!auctionType.is(BUY)) {
            result *= -1;
        }
    }
    return result;
};

export const rankComparator = (rank1, rank2) => {
    let result;
    if (rank1 === 0 && rank2 === 0) {
        result = 0;
    } else if (rank1 === 0) {
        result = 1;
    } else if (rank2 === 0) {
        result = -1;
    } else {
        result = rank1 - rank2;
    }
    return result;
};

export const winnerComparator = (bid1, bid2, t) => {
    const acceptedText = t('auctions.bids-list.accepted');
    const rejectedText = t('auctions.bids-list.rejected');
    const bid1WinnerToCompare = bid1.winner ? acceptedText : rejectedText;
    const bid2WinnerToCompare = bid2.winner ? acceptedText : rejectedText;
    return bid1WinnerToCompare.localeCompare(bid2WinnerToCompare);
};

export const modifiedByComparator = (bid1, bid2, t) => {
    const bid1TextToCompare = moment(bid1.lastModTime).isSame(bid1.creationTime)
        ? t('auctions.bids-list.no-modification')
        : bid1.lastModUser;
    const bid2TextToCompare = moment(bid2.lastModTime).isSame(bid2.creationTime)
        ? t('auctions.bids-list.no-modification')
        : bid2.lastModUser;
    return bid1TextToCompare.localeCompare(bid2TextToCompare);
};

export const cancellationReasonComparator = (bid1, bid2, t) => {
    const reason1 = getBidCancellationReason(bid1.cancellationReason).translate(t);
    const reason2 = getBidCancellationReason(bid2.cancellationReason).translate(t);
    return reason1.localeCompare(reason2);
};

export const compareBids = (bid1, bid2, sortingParam, auctionType, t) => {
    let compareBid1 = bid1;
    let compareBid2 = bid2;
    if (sortingParam.direction === Constants.SORT_DESC) {
        compareBid1 = bid2;
        compareBid2 = bid1;
    }
    switch (sortingParam.key) {
        case 'user':
            return createUserValue(compareBid1).localeCompare(createUserValue(compareBid2));
        case 'member':
            return compareBid1.member.memberId.localeCompare(compareBid2.member.memberId);
        case 'price':
            return priceComparator(compareBid1, compareBid2, auctionType);
        case 'quantity':
            return compareBid1.quantity - compareBid2.quantity;
        case 'bidVolume':
            return compareBid1.volume - compareBid2.volume;
        case 'quantityAllocated':
            return compareBid1.quantityAllocated - compareBid2.quantityAllocated;
        case 'balancingGroup':
            return compareBid1.member.balancingGroup.localeCompare(compareBid2.member.balancingGroup);
        case 'rank':
            return rankComparator(compareBid1.rank, compareBid2.rank);
        case 'winner':
            return winnerComparator(compareBid1, compareBid2, t);
        case 'modifiedBy':
            return modifiedByComparator(compareBid1, compareBid2, t);
        case 'cancellationReason':
            return cancellationReasonComparator(compareBid1, compareBid2, t);
        default:
            return 0;
    }
};
